import { BodyText, Stack, Title, Caption, Message } from '@kvdbil/components';
import React, { useCallback, useEffect } from 'react';
import BankIdAuthButton from '~/App/shared/components/Auth/components/BankIdAuthButton';
import { VerifyBankID } from '~/App/shared/components/Auth/VerifyBankID';
import { useTranslation } from '~/Locale';
import NoBankIdInfo from './NoBankIdInfo';
import {
  phoneNumber as phoneNumberSelector,
  isEmailVerified as isEmailVerifiedSelector,
  member as memberSelector
} from '~/App/shared/selectors/sessionSelector';
import { useDispatch, useSelector } from 'react-redux';
import {
  phoneVerificationIsUpdatingPhoneNumber,
  verifyPhoneNumberSelector
} from '~/App/shared/selectors/verifyPhoneNumberSelector';
import { PhoneNumberField } from '~/App/shared/components/Fields/shared';
import { regexPhoneNumber } from '~/helpers/regex';
import { useForm } from 'react-hook-form';
import {
  resetPhoneVerification,
  sendVerificationCode,
  updateMemberPhoneNumber
} from '~/App/shared/actions/verifyPhoneNumber';
import { usePrevious } from '~/App/shared/hooks/usePrevious';
import EnterCode from '~/App/shared/components/VerifyPhoneNumber/components/EnterCode';
import StatusPageLoading from '~/App/shared/components/StatusPageLoading';
import EmailVerificationRequiredMessage from '~/App/shared/components/VerifyPhoneNumber/components/EmailVerificationRequiredMessage';

type Form = {
  phoneNumber: string;
};

interface Props {
  isBankIdVerificationPending: boolean;
  setIsBankIdVerificationPending: (isPending: boolean) => void;
  isPhoneVerificationPending: boolean;
  setIsPhoneVerificationPending: (isPending: boolean) => void;
}

const BiddingPrivCustomerBankIdAndPhoneVerification = ({
  isBankIdVerificationPending,
  setIsBankIdVerificationPending,
  isPhoneVerificationPending,
  setIsPhoneVerificationPending
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const phoneNumber = useSelector(phoneNumberSelector);
  const isEmailVerified = useSelector(isEmailVerifiedSelector);
  const member = useSelector(memberSelector);
  const isUpdatingPhoneNumber = useSelector(
    phoneVerificationIsUpdatingPhoneNumber
  );
  const { error, generalError, isSendingCode } = useSelector(
    verifyPhoneNumberSelector
  );
  const previousIsUpdatingPhoneNumber = usePrevious<undefined | boolean>(
    isUpdatingPhoneNumber
  );
  const isError = error || generalError;

  const {
    control,
    handleSubmit,
    formState: { isDirty, isValid },
    reset
  } = useForm<Form>({
    mode: 'onChange',
    defaultValues: {
      phoneNumber
    }
  });

  useEffect(() => {
    if (previousIsUpdatingPhoneNumber && !isUpdatingPhoneNumber && !isError) {
      reset(undefined, { keepValues: true });
      setIsBankIdVerificationPending(true);
    }
  }, [
    previousIsUpdatingPhoneNumber,
    isUpdatingPhoneNumber,
    isError,
    setIsBankIdVerificationPending,
    reset
  ]);

  useEffect(() => {
    return () => dispatch(resetPhoneVerification());
  }, [dispatch]);

  const onSuccessBankIdVerificationCallback = useCallback(() => {
    setTimeout(() => {
      dispatch(sendVerificationCode());
      setIsPhoneVerificationPending(true);
      setIsBankIdVerificationPending(false);
    }, 2000);
  }, [setIsBankIdVerificationPending, setIsPhoneVerificationPending, dispatch]);

  if (isBankIdVerificationPending) {
    return (
      <VerifyBankID
        onClose={() => setIsBankIdVerificationPending(false)}
        onSuccess={onSuccessBankIdVerificationCallback}
      />
    );
  }

  if (isPhoneVerificationPending) {
    return (
      <>
        {isSendingCode && <StatusPageLoading />}
        {isError && (
          <Message type="error" fullWidth>
            {error || t('Something went wrong, please try again later.')}
          </Message>
        )}
        {!isSendingCode && !isError && (
          <EnterCode handleClose={() => setIsPhoneVerificationPending(false)} />
        )}
      </>
    );
  }

  const handleVerifyWithBankIdClick = ({ phoneNumber }: Form) => {
    isDirty && dispatch(updateMemberPhoneNumber(phoneNumber, member.addresses));
    !isDirty && setIsBankIdVerificationPending(true);
  };

  return (
    <Stack spacing={2} data-testid="customer-bankid-and-phone-verification">
      <Stack>
        <Title as="h3" style={{ textAlign: 'center' }}>
          {t('Verify your information')}
        </Title>
        <BodyText>{t('You must verify your information to proceed.')}</BodyText>
      </Stack>
      <Stack rowGap={0.25}>
        <PhoneNumberField
          control={control}
          name="phoneNumber"
          label={t('Mobile number')}
          t={t}
          rules={{
            pattern: {
              value: regexPhoneNumber,
              message: t('Invalid phone number')
            }
          }}
          isDisabled={!isEmailVerified}
        />

        <Caption>
          {t(
            'We need your mobile number to be able to contact you if you win the bidding and to send out bidding information.'
          )}
        </Caption>
      </Stack>

      <Stack>
        {isError && (
          <Message type="error" fullWidth>
            {error || t('Something went wrong, please try again later.')}
          </Message>
        )}

        {!isEmailVerified && <EmailVerificationRequiredMessage />}

        <div>
          <BankIdAuthButton
            fullWidth
            onClick={handleSubmit(handleVerifyWithBankIdClick)}
            isLoading={isUpdatingPhoneNumber}
            isDisabled={!isValid || !isEmailVerified}
          >
            {t('Verify with BankID')}
          </BankIdAuthButton>
          <Caption>
            {t(
              'We need to verify your account with BankID to ensure your identity and make bidding safe for all parties.'
            )}
          </Caption>
        </div>
        <NoBankIdInfo />
      </Stack>
    </Stack>
  );
};

export default BiddingPrivCustomerBankIdAndPhoneVerification;
